/**
 * Main-thread clients for the workers. When Web Workers are unavailable (tests, very old
 * browsers) the same services run in the main thread.
 */
import type { Design } from '../engine/model/types';
import type { Analysis, DigitizeOptions, DigitizeResult } from '../imaging/digitize';
import type { Raster } from '../imaging/raster';
import type { FormatId } from '../formats';
import { type Lang, getLang } from '../i18n/i18n';
import type { DigitizeResponse, ExportResponse, GenerateResult, StitchRequest, StitchResponse } from './protocol';
import type { StitchService } from './stitchService';

const hasWorker = typeof Worker !== 'undefined' && typeof window !== 'undefined';

type Pending = { resolve: (v: unknown) => void; reject: (e: Error) => void };

export class StitchClient {
  private worker: Worker | null = null;
  /** In-thread fallback, loaded lazily so the engine is not bundled into the main chunk. */
  private fallback: Promise<StitchService> | null = null;
  private pending = new Map<number, Pending>();
  private nextId = 1;
  private latest = 0;

  constructor() {
    if (hasWorker) {
      try {
        this.worker = new Worker(new URL('./stitch.worker.ts', import.meta.url), { type: 'module' });
        this.worker.onmessage = (e: MessageEvent<StitchResponse>) => this.onMessage(e.data);
        this.worker.onerror = (e) => {
          for (const p of this.pending.values()) p.reject(new Error(e.message || 'Feil i stingarbeider'));
          this.pending.clear();
        };
      } catch {
        this.worker = null;
      }
    }
    if (!this.worker) this.fallback = import('./stitchService').then((m) => new m.StitchService());
  }

  private onMessage(m: StitchResponse): void {
    const p = this.pending.get(m.id);
    if (!p) return;
    this.pending.delete(m.id);
    if (m.type === 'error') p.reject(new Error(m.message));
    else if (m.type === 'cancelled') p.resolve(null);
    else p.resolve(m.result);
  }

  private request<T>(msg: StitchRequest): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.pending.set(msg.id, { resolve: resolve as (v: unknown) => void, reject });
      this.worker!.postMessage(msg);
    });
  }

  /** Generates stitches. Resolves null if superseded by a newer generate call. */
  async generate(design: Design): Promise<GenerateResult | null> {
    const id = this.nextId++;
    this.latest = id;
    if (this.fallback) {
      const svc = await this.fallback;
      svc.setLang(getLang());
      return svc.generate(design, () => this.latest === id);
    }
    return this.request<GenerateResult | null>({ type: 'generate', id, design, lang: getLang() });
  }

  async export(design: Design, format: FormatId): Promise<ExportResponse> {
    const id = this.nextId++;
    if (this.fallback) {
      const svc = await this.fallback;
      svc.setLang(getLang());
      return svc.export(design, format);
    }
    return this.request<ExportResponse>({ type: 'export', id, design, format, lang: getLang() });
  }

  dispose(): void {
    this.worker?.terminate();
  }
}

export interface DigitizeJob<T> {
  promise: Promise<T>;
  cancel: () => void;
}

export class CancelledError extends Error {
  constructor() {
    super('Avbrutt');
  }
}

function runDigitizeJob<T>(
  msg: { type: 'analyze' | 'digitize'; image: Raster; options: DigitizeOptions; lang: Lang },
  onProgress: (phase: string, fraction: number) => void,
): DigitizeJob<T> {
  if (!hasWorker) {
    let cancelled = false;
    const promise = import('../imaging/digitize').then((m) => {
      if (cancelled) throw new CancelledError();
      return (msg.type === 'analyze' ? m.analyze(msg.image, msg.options) : m.digitize(msg.image, msg.options, onProgress)) as T;
    });
    return { promise, cancel: () => (cancelled = true) };
  }
  const worker = new Worker(new URL('./digitize.worker.ts', import.meta.url), { type: 'module' });
  let rejectFn: (e: Error) => void = () => {};
  const promise = new Promise<T>((resolve, reject) => {
    rejectFn = reject;
    worker.onmessage = (e: MessageEvent<DigitizeResponse>) => {
      const m = e.data;
      if (m.type === 'progress') onProgress(m.phase, m.fraction);
      else {
        worker.terminate();
        if (m.type === 'error') reject(new Error(m.message));
        else resolve(m.result as T);
      }
    };
    worker.onerror = (e) => {
      worker.terminate();
      reject(new Error(e.message || 'Feil i digitaliseringsarbeider'));
    };
    // copy the pixels so the caller keeps its image
    const image = { width: msg.image.width, height: msg.image.height, data: msg.image.data.slice() };
    worker.postMessage({ ...msg, image }, [image.data.buffer]);
  });
  return {
    promise,
    cancel: () => {
      worker.terminate();
      rejectFn(new CancelledError());
    },
  };
}

export const startAnalyze = (image: Raster, options: DigitizeOptions): DigitizeJob<Analysis> =>
  runDigitizeJob<Analysis>({ type: 'analyze', image, options, lang: getLang() }, () => {});

export const startDigitize = (image: Raster, options: DigitizeOptions, onProgress: (phase: string, f: number) => void): DigitizeJob<DigitizeResult> =>
  runDigitizeJob<DigitizeResult>({ type: 'digitize', image, options, lang: getLang() }, onProgress);
